/**
 * Инспектор стиля/подписи выбранной фичи (update feature API).
 */
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { TacticalFeature } from "@/services/tactical.service";

type Props = {
  feature: TacticalFeature | null;
  canManage: boolean;
  busy?: boolean;
  onSave: (patch: {
    label?: string;
    stroke_color?: string;
    fill_color?: string;
    fill_opacity?: number;
  }) => void;
};

export function TacticalFeatureStyleForm({ feature, canManage, busy = false, onSave }: Props) {
  const [label, setLabel] = useState("");
  const [stroke, setStroke] = useState("#2563eb");
  const [fill, setFill] = useState("#2563eb");
  const [opacity, setOpacity] = useState("0.2");

  useEffect(() => {
    const p = feature?.properties;
    setLabel(p?.label ?? "");
    setStroke(p?.strokeColor ?? "#2563eb");
    setFill(p?.fillColor ?? p?.strokeColor ?? "#2563eb");
    setOpacity(String(p?.fillOpacity ?? 0.2));
  }, [feature?.properties?.id, feature?.properties?.revision]);

  if (feature == null) {
    return (
      <p className="px-1 text-xs text-muted-foreground">
        Выберите фичу на карте или в списке слева — здесь появятся подпись и стиль.
      </p>
    );
  }

  const locked = Boolean(feature.properties?.effectivelyLocked);

  return (
    <form
      className="space-y-2 px-1"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canManage || locked) return;
        const op = Number(opacity);
        onSave({
          label: label.trim(),
          stroke_color: stroke,
          fill_color: fill,
          fill_opacity: Number.isFinite(op) ? Math.min(1, Math.max(0, op)) : 0.2,
        });
      }}
    >
      <p className="text-[10px] text-muted-foreground">
        #{feature.properties?.id} · {feature.properties?.kind}
        {locked ? " · заблокирована" : ""}
      </p>
      <div className="space-y-1">
        <Label className="text-[10px]">Подпись</Label>
        <Input
          className="h-8 text-xs"
          value={label}
          disabled={!canManage || locked || busy}
          onChange={(e) => setLabel(e.target.value)}
        />
      </div>
      <div className="flex gap-2">
        <div className="space-y-1">
          <Label className="text-[10px]">Обводка</Label>
          <Input
            type="color"
            className="h-8 w-14 p-1"
            value={stroke}
            disabled={!canManage || locked || busy}
            onChange={(e) => setStroke(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px]">Заливка</Label>
          <Input
            type="color"
            className="h-8 w-14 p-1"
            value={fill}
            disabled={!canManage || locked || busy}
            onChange={(e) => setFill(e.target.value)}
          />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <Label className="text-[10px]">Opacity</Label>
          <Input
            className="h-8 text-xs"
            value={opacity}
            disabled={!canManage || locked || busy}
            onChange={(e) => setOpacity(e.target.value)}
          />
        </div>
      </div>
      {canManage && (
        <Button type="submit" size="sm" className="h-7 w-full" disabled={locked || busy}>
          {busy ? "Сохранение…" : "Сохранить стиль"}
        </Button>
      )}
    </form>
  );
}
